import { AudienceType, RoleBrief } from '../types/exercise';

/**
 * Feature flag for audience gating functionality
 */
export const FEATURE_AUDIENCE_GATING = true;

/**
 * Board/CMT role patterns for inference
 */
const BOARD_CMT_ROLES = [
  'board', 'director', 'cmt', 'executive', 'sponsor', 'ceo', 'cfo', 'coo',
  'chief', 'president', 'vice president', 'managing director', 'chairman',
  'chairwoman', 'chair', 'council', 'committee', 'governance'
];

const SITE_ROLES = [
  'site lead', 'store', 'clinic', 'office', 'staff', 'reception', 'security',
  'shift supervisor', 'team leader', 'floor manager', 'assistant', 'technician'
];

/**
 * Infer audience type from role briefs content
 */
export function inferAudienceType(roleBriefs: RoleBrief[]): AudienceType {
  if (!roleBriefs || roleBriefs.length === 0) {
    return 'site'; // Default fallback
  }

  let boardCmtCount = 0;
  let siteCount = 0;

  roleBriefs.forEach(brief => {
    const roleText = brief.role.toLowerCase();
    const purposeText = brief.purpose.toLowerCase();
    
    // Check for board/CMT indicators
    const hasBoardCmt = BOARD_CMT_ROLES.some(pattern => 
      roleText.includes(pattern) || purposeText.includes(pattern)
    );
    
    // Check for site indicators
    const hasSite = SITE_ROLES.some(pattern => 
      roleText.includes(pattern) || purposeText.includes(pattern)
    );

    if (hasBoardCmt) boardCmtCount++;
    if (hasSite) siteCount++;
  });

  // Decision logic
  if (boardCmtCount > 0 && siteCount === 0) {
    return boardCmtCount >= 3 ? 'board' : 'cmt';
  }
  
  if (siteCount > 0 && boardCmtCount === 0) {
    return 'site';
  }
  
  if (boardCmtCount > 0 && siteCount > 0) {
    return 'mixed';
  }

  return 'site'; // Default fallback
}

/**
 * Get audience type with fallback inference
 */
export function getAudienceType(
  meta?: { audienceType?: AudienceType },
  roleBriefs?: RoleBrief[]
): AudienceType {
  // Use authoritative field if present
  if (meta?.audienceType) {
    return meta.audienceType;
  }

  // Infer from role briefs
  if (roleBriefs) {
    return inferAudienceType(roleBriefs);
  }

  return 'site'; // Default
}

/**
 * Check if role briefs should be displayed
 */
export function shouldShowRoleBriefs(audienceType: AudienceType): boolean {
  if (!FEATURE_AUDIENCE_GATING) {
    return true; // Show all when feature disabled
  }
  
  return audienceType === 'board' || audienceType === 'cmt';
}

/**
 * Get site participant roles for simplified display
 */
export function getSiteParticipantRoles() {
  return [
    {
      role: 'Site Lead / Store Leader',
      purpose: 'Owns evacuation decision, headcount, emergency calls; escalates when headcount incomplete >3 min or smoke in public area.'
    },
    {
      role: 'Staff',
      purpose: 'Life safety, customer/patient direction, assembly, headcount support.'
    }
  ];
}

